import BundleCard from '@Components/Cards/BundleCard'
import SkeletonCard from '@Components/Utilities/Skeleton/Components/SkeletonCard'
import SkeletonCarousel from '@Components/Utilities/Skeleton/Components/SkeletonCarousel'
import { Swiper } from '@Components/Utilities/Swiper'
import routes from '@Constants/Routes'
import { useGetFeaturedBundlesQuery } from '@Features/Bundle/Api/BundleApi'
import { FlexLayout, Typography } from '@eduact/ed-system'
import { Icon, Icons } from '@eduact/ed-components'
import { useAppSelector } from '@Hooks/ReduxHooks'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import styled from 'styled-components'
import { space, SpaceProps } from 'styled-system'

/**
 * Admin-curated featured bundles. Self-hiding: renders nothing when the
 * bundles platform is off or no bundle is featured.
 */
const FeaturedBundlesCarousel: React.FC = () => {
	const [t] = useTranslation('bundles')
	const navigate = useNavigate()
	const { standalone_courses_enabled, bundles_enabled } = useAppSelector(s => s.settings)
	const bundlesEnabled = standalone_courses_enabled && bundles_enabled
	const { data, isLoading, isSuccess } = useGetFeaturedBundlesQuery(undefined, { skip: !bundlesEnabled })

	if (!bundlesEnabled) return null
	if (!isLoading && (!data || data.data.length === 0)) return null

	return (
		<SkeletonCarousel padding={0} gridGap={'1rem'} numberOfItems={6} isLoading={isLoading} renderComponent={<SkeletonCard />}>
			<FlexLayout mb={'4.063rem'} alignItems={'center'} justifyContent={'space-between'}>
				<SectionTitle>{t('featured_title')}</SectionTitle>
				<StyledViewAll
					mx={{ lg: '8.375rem' }}
					onClick={() => navigate(routes.bundles)}
					alignItems={'center'}
					justifyContent={'center'}
					gridGap={'0.2rem'}
				>
					<Typography fontSize={{ lg: '1.125rem' }} fontWeight={'600'} textDecoration={'underline'}>
						{t('view_all')}
					</Typography>
					<Icon color="dark" size="1.5rem">
						<Icons.RightArrow />
					</Icon>
				</StyledViewAll>
			</FlexLayout>
			<SectionWrapper px={{ lg: '3.875rem' }} mx={{ sm: '-1rem', lg: '' }}>
				<Swiper perSlide={{ sm: 1 }} overlayArrows withArrows withProgress>
					{isSuccess &&
						data &&
						data.data.map((bundle, index) => (
							<Swiper.Item key={`${bundle.id}-${index}-featured-bundles`} swipe="1">
								<BundleCard bundle={bundle} />
							</Swiper.Item>
						))}
				</Swiper>
			</SectionWrapper>
		</SkeletonCarousel>
	)
}

export default FeaturedBundlesCarousel

const SectionTitle = styled.h1`
	font-weight: bold;
	${({ theme }) => `${theme.mediaQueries.large}{
		padding : 0 8.375rem;
		font-size: 1.5rem;
	}`}
`

const SectionWrapper = styled.div<SpaceProps>`
	${space}
`

const StyledViewAll = styled(FlexLayout).attrs(p => ({ dir: document.documentElement.dir }))`
	cursor: pointer;
	&[dir='rtl'] {
		svg {
			transform: scale(-1, 1);
		}
	}
`
